import React, { useRef, useState } from "react";
import { Maximize2, Download, GitCompare, Monitor, Tablet, Smartphone, ChevronDown, AlignLeft } from "lucide-react";
import CustomTooltip from "../Tooltip/CustomTooltip";
import AnchoredPanel from "../shared/AnchoredPanel/AnchoredPanel";

const BREAKPOINTS = [
  { key: "fluid",   label: "Fluid",              width: null, Icon: Monitor },
  { key: "desktop", label: "Desktop (1280px)",   width: 1280, Icon: Monitor },
  { key: "tablet",  label: "Tablet (768px)",     width: 768,  Icon: Tablet },
  { key: "mobile",  label: "Mobile (390px)",     width: 390,  Icon: Smartphone },
];

// Unified icon button styles — used across the canvas header for visual consistency.
export const NEBULA_BTN_CLS =
  "photon-icon-button flex items-center justify-center p-1.5 bg-fill-white dark:bg-fill-editor-surface border-[0.5px] border-edge-border dark:border-edge-editor-border text-ink-ink dark:text-ink-white rounded-lg hover:bg-fill-gray-100 dark:hover:bg-fill-editor-hover transition-colors";
export const NEBULA_BTN_ACTIVE_CLS =
  "photon-icon-button photon-control-active flex items-center justify-center p-1.5 bg-fill-canvas-accent/15 border-[0.5px] border-edge-canvas-accent/40 text-ink-canvas-accent rounded-lg hover:bg-fill-canvas-accent/25 transition-colors";

/**
 * Icon action buttons for the Nebula canvas header bar.
 *
 * Props:
 *   artifact       — current artifact ({ type, content })
 *   canDiff        — boolean, whether this artifact type supports diff
 *   diffMode       — boolean, whether diff view is currently active
 *   hasPrevious    — boolean, whether a diffable previous artifact exists
 *   onToggleDiff   — called when diff toggle is clicked
 *   onFullscreen   — called when fullscreen button is clicked
 *   onDownload     — called when download button is clicked
 *   previewWidth   — null | 1280 | 768 | 390 — active breakpoint width
 *   onPreviewWidth — called with width (or null to clear)
 */
const NebulaCanvasControls = ({
  artifact,
  canDiff = false,
  diffMode = false,
  hasPrevious = false,
  isFullscreen = false,
  hasReportContent = false,
  reportMode = false,
  onToggleReport,
  onToggleDiff,
  onFullscreen,
  onDownload,
  previewWidth = null,
  onPreviewWidth,
}) => {
  const bpAnchorRef = useRef(null);
  const [bpOpen, setBpOpen] = useState(false);

  if (!artifact) return null;
  // Hide controls when in native fullscreen — ESC exits, no UI clutter needed
  if (isFullscreen) return null;

  const showBreakpoints = onPreviewWidth && (artifact.type === "html" || artifact.type === "react");
  const activeBp = BREAKPOINTS.find(b => b.width === previewWidth) ?? BREAKPOINTS[0];
  const ActiveBpIcon = activeBp.Icon;

  return (
    <div className="flex items-center gap-1">
      {/* Report mode toggle — only when surrounding text exists */}
      {hasReportContent && onToggleReport && (
          <CustomTooltip content={reportMode ? "Hide context" : "Show context"} side="bottom">
          <button
            className={reportMode ? NEBULA_BTN_ACTIVE_CLS : NEBULA_BTN_CLS}
            onClick={onToggleReport}
            aria-label="Toggle report mode"
          >
            <AlignLeft className="w-3.5 h-3.5" />
          </button>
        </CustomTooltip>
      )}

      {/* Diff toggle — only for diffable types when a previous version exists */}
      {canDiff && hasPrevious && (
        <CustomTooltip content={diffMode ? "Exit diff" : "Diff with previous version"} side="bottom">
          <button
            className={diffMode ? NEBULA_BTN_ACTIVE_CLS : NEBULA_BTN_CLS}
            onClick={onToggleDiff}
            aria-label="Toggle diff view"
          >
            <GitCompare className="w-3.5 h-3.5" />
          </button>
        </CustomTooltip>
      )}

      {/* Breakpoint dropdown — html and react only */}
      {showBreakpoints && (
        <>
          <CustomTooltip content="Viewport size" side="bottom">
            <button
              ref={bpAnchorRef}
              onClick={() => setBpOpen(v => !v)}
              className={previewWidth ? NEBULA_BTN_ACTIVE_CLS : NEBULA_BTN_CLS}
              aria-label="Viewport size"
            >
              <ActiveBpIcon className="w-3.5 h-3.5" />
              <ChevronDown className={`w-3 h-3 ml-0.5 transition-transform ${bpOpen ? "rotate-180" : ""}`} />
            </button>
          </CustomTooltip>
          <AnchoredPanel
            anchorRef={bpAnchorRef}
            isOpen={bpOpen}
            onClose={() => setBpOpen(false)}
            width={180}
            align="right"
            className="py-1"
          >
            {BREAKPOINTS.map(({ key, label, width, Icon }) => {
              const isActive = previewWidth === width;
              return (
                <button
                  key={key}
                  onClick={() => { onPreviewWidth(width); setBpOpen(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                    isActive
                      ? "bg-fill-canvas-accent/15 text-ink-canvas-accent"
                      : "text-ink-gray-700 dark:text-ink-gray-200 hover:bg-fill-gray-100 dark:hover:bg-fill-canvas-menu-hover"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="flex-1 text-left">{label}</span>
                </button>
              );
            })}
          </AnchoredPanel>
        </>
      )}

      {/* Download */}
      <CustomTooltip content="Download" side="bottom">
        <button className={NEBULA_BTN_CLS} onClick={onDownload} aria-label="Download">
          <Download className="w-3.5 h-3.5" />
        </button>
      </CustomTooltip>

      {/* Fullscreen */}
      <CustomTooltip content="Fullscreen" side="bottom">
        <button className={NEBULA_BTN_CLS} onClick={onFullscreen} aria-label="Fullscreen">
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </CustomTooltip>
    </div>
  );
};

export default NebulaCanvasControls;
